import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnDestroy, OnInit, OnChanges, AfterViewInit, HostListener, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../icons/icon.component';
import { ModalComponent } from '../modal/modal.component';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ExotelApiService } from '../../../core/services/exotel/exotel-api.service';
import { Router } from '@angular/router';

export interface CallMetadata {
  patientName?: string;
  telecallerName?: string;
  callDate?: string;
  callTime?: string;
  duration?: string;
  callType?: string;
  outcome?: string;
}

@Component({
  selector: 'app-audio-player-modal',
  standalone: true,
  imports: [CommonModule, IconComponent, FormsModule, NgSelectModule, ModalComponent],
  templateUrl: './audio-player-modal.component.html',
  styleUrl: './audio-player-modal.component.css',
})
export class AudioPlayerModalComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() audioUrl: string = '';
  @Input() title: string = 'Call Recording';
  @Input() subtitle: string = '';
  @Input() metadata?: CallMetadata;

  @Output() close = new EventEmitter<void>();

  // Inject services
  private authService = inject(AuthService);
  private exotelService = inject(ExotelApiService);
  private router = inject(Router);

  @ViewChild('audioElement', { static: false }) audioElement!: ElementRef<HTMLAudioElement>;
  @ViewChild('progressBar', { static: false }) progressBar!: ElementRef<HTMLDivElement>;

  // Audio state
  isPlaying: boolean = false;
  isLoading: boolean = false;
  hasError: boolean = false;
  errorMessage: string = '';
  isAuthenticationError: boolean = false;
  currentTime: number = 0;
  duration: number = 0;
  progress: number = 0;
  volume: number = 1;
  playbackRate: number = 0.5;

  // Store original recording URL for authenticated fetch
  private originalRecordingUrl: string = '';

  // Flag to track if view is initialized
  private viewInitialized: boolean = false;

  // Playback rates
  playbackRates = [0.5, 0.75, 1, 1.25, 1.5, 2];

  // Skip intervals (configurable)
  skipBackwardSeconds = 5;
  skipForwardSeconds = 5;

  // Progress bar dragging state
  isDragging = false;
  hoverTime: number | null = null;

  // UI state
  showKeyboardShortcuts = false;
  showMoreMenu = false;
  showMetadataPanel = false;
  readonly Math = Math;

  toggleMoreMenu(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    this.showMoreMenu = !this.showMoreMenu;
  }

  closeMoreMenu() {
    this.showMoreMenu = false;
  }

  toggleMetadataPanel() {
    this.showMetadataPanel = !this.showMetadataPanel;
    this.showMoreMenu = false;
  }

  toggleKeyboardShortcuts() {
    this.showKeyboardShortcuts = !this.showKeyboardShortcuts;
    this.showMoreMenu = false;
  }

  cyclePlaybackRate() {
    const currentIndex = this.playbackRates.indexOf(this.playbackRate);
    const nextIndex = (currentIndex + 1) % this.playbackRates.length;
    this.setPlaybackRate(this.playbackRates[nextIndex]);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (this.showMoreMenu && !target.closest('.more-menu-container')) {
      this.showMoreMenu = false;
    }
  }

  ngOnInit() {
    // Don't initialize here - wait for view to be ready
  }

  ngAfterViewInit() {
    // View is now initialized
    this.viewInitialized = true;

    // Initialize audio if modal is already open
    if (this.isOpen && this.audioUrl) {
      this.initializeAudioWhenReady();
    }
  }

  ngOnChanges() {
    // Handle changes after view is initialized
    if (this.viewInitialized) {
      if (this.isOpen && this.audioUrl) {
        this.initializeAudioWhenReady();
      } else if (!this.isOpen) {
        this.cleanup();
      }
    }
  }

  /**
   * Initialize audio when element is ready
   * Uses setTimeout to ensure audio element is rendered (it's inside @if block)
   */
  private initializeAudioWhenReady() {
    setTimeout(() => {
      if (this.audioElement?.nativeElement) {
        this.checkAuthenticationAndInitialize();
      } else {
        console.warn('Audio element not yet available, retrying...');
        // Retry once more after a longer delay
        setTimeout(() => {
          if (this.audioElement?.nativeElement) {
            this.checkAuthenticationAndInitialize();
          } else {
            console.error('Audio element failed to initialize');
            this.hasError = true;
            this.errorMessage = 'Failed to initialize audio player. Please try again.';
          }
        }, 200);
      }
    }, 50);
  }

  /**
   * Check if user is authenticated before initializing audio
   */
  private checkAuthenticationAndInitialize() {
    if (!this.authService.isUserAuthenticated()) {
      this.handleAuthenticationError();
      return;
    }

    const token = this.authService.getToken();
    if (!token) {
      this.handleAuthenticationError();
      return;
    }

    // Check if token is expired by trying to decode it
    if (this.isTokenExpired(token)) {
      this.handleTokenExpired();
      return;
    }

    // Store the original recording URL (from the proxy URL)
    // Extract the recording_url parameter from the audioUrl
    try {
      const url = new URL(this.audioUrl, window.location.origin);
      const recordingUrl = url.searchParams.get('recording_url');
      if (recordingUrl) {
        this.originalRecordingUrl = recordingUrl;
      }
    } catch (error) {
      console.warn('Could not parse recording URL:', error);
    }

    // User is authenticated, proceed with audio initialization
    this.initializeAudio();
  }

  /**
   * Handle authentication error
   */
  private handleAuthenticationError() {
    this.hasError = true;
    this.isAuthenticationError = true;
    this.errorMessage = 'You must be logged in to play recordings. Please log in and try again.';
    this.isLoading = false;
  }

  /**
   * Handle expired token
   */
  private handleTokenExpired() {
    this.hasError = true;
    this.isAuthenticationError = true;
    this.errorMessage = 'Your session has expired. Please log in again to play recordings.';
    this.isLoading = false;
  }

  /**
   * Check if JWT token is expired
   */
  private isTokenExpired(token: string): boolean {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const decoded = JSON.parse(jsonPayload);

      if (!decoded || !decoded.exp) {
        return true;
      }

      const expiryTime = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      return currentTime >= expiryTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true; // Assume expired if we can't decode
    }
  }

  /**
   * Redirect to login page
   */
  redirectToLogin() {
    this.onClose();
    this.router.navigate(['/auth/login']);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  /**
   * Handle keyboard shortcuts
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Only handle keyboard events when modal is open
    if (!this.isOpen || this.hasError) return;

    // Prevent default for handled keys
    const handledKeys = [' ', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'r', 'R'];
    if (handledKeys.includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case ' ': // Space - Play/Pause
        this.togglePlayPause();
        break;
      case 'ArrowLeft': // Left Arrow - Rewind
        this.rewind(event.shiftKey ? 5 : this.skipBackwardSeconds);
        break;
      case 'ArrowRight': // Right Arrow - Fast Forward
        this.fastForward(event.shiftKey ? 5 : this.skipForwardSeconds);
        break;
      case 'ArrowUp': // Up Arrow - Increase Volume
        this.adjustVolume(0.1);
        break;
      case 'ArrowDown': // Down Arrow - Decrease Volume
        this.adjustVolume(-0.1);
        break;
      case 'r':
      case 'R': // R - Replay
        this.replay();
        break;
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
        // Number keys - Jump to percentage (0 = 0%, 1 = 10%, ..., 9 = 90%)
        const percent = parseInt(event.key) / 10;
        this.jumpToPercent(percent);
        break;
    }
  }

  /**
   * Adjust volume by delta
   */
  private adjustVolume(delta: number) {
    if (!this.audioElement?.nativeElement) return;

    this.volume = Math.max(0, Math.min(1, this.volume + delta));
    this.audioElement.nativeElement.volume = this.volume;
  }

  /**
   * Jump to a specific percentage of the audio
   */
  private jumpToPercent(percent: number) {
    if (!this.audioElement?.nativeElement) return;

    const newTime = this.duration * percent;
    this.audioElement.nativeElement.currentTime = newTime;
  }

  initializeAudio() {
    if (this.audioElement?.nativeElement && this.audioUrl) {
      this.resetAudioState();

      // If we have the original recording URL, fetch it with authentication
      if (this.originalRecordingUrl) {
        this.loadAudioWithAuthentication();
      } else {
        // Fallback to direct URL loading
        this.loadAudioDirect();
      }
    } else {
      console.error('Cannot initialize audio - missing element or URL');
    }
  }

  /**
   * Load audio with authentication using blob URL
   */
  private loadAudioWithAuthentication() {
    this.isLoading = true;

    this.exotelService.getRecordingBlob(this.originalRecordingUrl, false).subscribe({
      next: (blob: Blob) => {
        this.loadAudioFromBlob(blob);
      },
      error: (error) => {
        console.error('Error fetching authenticated recording:', error);

        // Check if it's a 403 error
        if (error.status === 403) {
          this.handleAuthenticationError();
        } else {
          this.hasError = true;
          this.errorMessage = 'Failed to load recording. Please try again.';
          this.isLoading = false;
        }
      }
    });
  }

  /**
   * Load audio from blob using blob URL
   */
  private loadAudioFromBlob(blob: Blob) {
    const audio = this.audioElement?.nativeElement;
    if (!audio) {
      console.error('Audio element not found');
      return;
    }

    try {
      // Ensure blob has correct MIME type
      let audioBlob = blob;
      if (!blob.type || blob.type === 'application/octet-stream') {
        audioBlob = new Blob([blob], { type: 'audio/mpeg' });
      }

      // Create a blob URL for the audio
      const blobUrl = URL.createObjectURL(audioBlob);

      // Set up listeners before setting src
      this.setupAudioListeners();

      // Set the audio source to the blob URL
      audio.src = blobUrl;

      // Force load the audio
      audio.load();
    } catch (error) {
      console.error('Error creating blob URL:', error);
      this.hasError = true;
      this.errorMessage = 'Failed to process audio file.';
      this.isLoading = false;
    }
  }

  /**
   * Load audio directly from URL (fallback)
   */
  private loadAudioDirect() {
    const audio = this.audioElement?.nativeElement;
    if (!audio) return;

    // Set crossOrigin to handle CORS issues
    audio.crossOrigin = 'anonymous';

    // Set up listeners before setting src to catch all events
    this.setupAudioListeners();

    // Set the audio source
    audio.src = this.audioUrl;

    // Force load the audio
    audio.load();
  }

  private resetAudioState() {
    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';
    this.isAuthenticationError = false;
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.progress = 0;
  }

  private setupAudioListeners() {
    const audio = this.audioElement.nativeElement;

    // Remove existing listeners to prevent duplicates
    audio.removeEventListener('loadstart', this.onLoadStart);
    audio.removeEventListener('loadedmetadata', this.onLoadedMetadata);
    audio.removeEventListener('canplay', this.onCanPlay);
    audio.removeEventListener('timeupdate', this.onTimeUpdate);
    audio.removeEventListener('ended', this.onEnded);
    audio.removeEventListener('error', this.onError);
    audio.removeEventListener('stalled', this.onStalled);
    audio.removeEventListener('waiting', this.onWaiting);

    // Add event listeners
    audio.addEventListener('loadstart', this.onLoadStart);
    audio.addEventListener('loadedmetadata', this.onLoadedMetadata);
    audio.addEventListener('canplay', this.onCanPlay);
    audio.addEventListener('timeupdate', this.onTimeUpdate);
    audio.addEventListener('ended', this.onEnded);
    audio.addEventListener('error', this.onError);
    audio.addEventListener('stalled', this.onStalled);
    audio.addEventListener('waiting', this.onWaiting);
  }

  private onLoadStart = () => {
    this.isLoading = true;
    this.hasError = false;
  };

  private onLoadedMetadata = () => {
    const audio = this.audioElement.nativeElement;
    this.duration = audio.duration;
  };

  private onCanPlay = () => {
    this.isLoading = false;
  };

  private onTimeUpdate = () => {
    const audio = this.audioElement.nativeElement;
    this.currentTime = audio.currentTime;
    if (this.duration > 0) {
      this.progress = (audio.currentTime / this.duration) * 100;
    }
  };

  private onEnded = () => {
    this.isPlaying = false;
    this.currentTime = 0;
    this.progress = 0;
  };

  private onError = (event: any) => {
    this.isLoading = false;
    this.hasError = true;
    this.isPlaying = false;

    const audio = this.audioElement.nativeElement;
    const error = audio.error;

    if (error) {
      switch (error.code) {
        case error.MEDIA_ERR_ABORTED:
          this.errorMessage = 'Audio playback was aborted';
          break;
        case error.MEDIA_ERR_NETWORK:
          this.errorMessage = 'Network error occurred while loading audio';
          break;
        case error.MEDIA_ERR_DECODE:
          this.errorMessage = 'Audio format not supported or corrupted';
          break;
        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
          this.errorMessage = 'Audio source not supported';
          break;
        default:
          this.errorMessage = 'Unknown audio error occurred';
      }
    } else {
      this.errorMessage = 'Failed to load audio recording';
    }

    console.error('Audio error:', this.errorMessage, event);
  };

  private onStalled = () => {
    console.warn('Audio loading stalled');
  };

  private onWaiting = () => {
    this.isLoading = true;
  };

  togglePlayPause() {
    if (!this.audioElement?.nativeElement || this.hasError) return;

    const audio = this.audioElement.nativeElement;

    if (this.isPlaying) {
      audio.pause();
      this.isPlaying = false;
    } else {
      // Check if audio is ready to play
      if (audio.readyState >= 2) { // HAVE_CURRENT_DATA or higher
        this.playAudio();
      } else {
        // Wait for audio to be ready
        this.isLoading = true;
        const onCanPlay = () => {
          audio.removeEventListener('canplay', onCanPlay);
          this.playAudio();
        };
        audio.addEventListener('canplay', onCanPlay);
      }
    }
  }

  private playAudio() {
    const audio = this.audioElement.nativeElement;

    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          this.isPlaying = true;
          this.isLoading = false;
        })
        .catch((error) => {
          console.error('Error playing audio:', error);
          this.isPlaying = false;
          this.isLoading = false;
          this.hasError = true;
          this.errorMessage = 'Failed to play audio. Please try again.';
        });
    } else {
      this.isPlaying = true;
      this.isLoading = false;
    }
  }

  /**
   * Seek to a specific position in the audio
   */
  seek(event: MouseEvent) {
    if (!this.audioElement?.nativeElement || !this.progressBar?.nativeElement) return;

    const rect = this.progressBar.nativeElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const newTime = percent * this.duration;

    this.audioElement.nativeElement.currentTime = newTime;
  }

  /**
   * Start dragging the progress bar
   */
  startDrag(event: MouseEvent | TouchEvent) {
    event.preventDefault();
    this.isDragging = true;

    // Handle initial seek
    if (event instanceof MouseEvent) {
      this.seek(event);
    } else if (event instanceof TouchEvent && event.touches.length > 0) {
      this.seekTouch(event.touches[0]);
    }

    // Add global mouse/touch move and up listeners
    const onMouseMove = (e: MouseEvent) => {
      if (this.isDragging) {
        this.seek(e);
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (this.isDragging && e.touches.length > 0) {
        this.seekTouch(e.touches[0]);
      }
    };

    const onEnd = () => {
      this.isDragging = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onEnd);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onEnd);
  }

  /**
   * Seek using touch event
   */
  private seekTouch(touch: Touch) {
    if (!this.audioElement?.nativeElement || !this.progressBar?.nativeElement) return;

    const rect = this.progressBar.nativeElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    const newTime = percent * this.duration;

    this.audioElement.nativeElement.currentTime = newTime;
  }

  /**
   * Show hover preview on progress bar
   */
  showHoverPreview(event: MouseEvent) {
    if (!this.progressBar?.nativeElement || this.isDragging) return;

    const rect = this.progressBar.nativeElement.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    this.hoverTime = percent * this.duration;
  }

  /**
   * Hide hover preview
   */
  hideHoverPreview() {
    this.hoverTime = null;
  }

  setVolume(event: Event) {
    const target = event.target as HTMLInputElement;
    this.volume = parseFloat(target.value);
    if (this.audioElement?.nativeElement) {
      this.audioElement.nativeElement.volume = this.volume;
    }
  }

  setPlaybackRate(rate: number) {
    this.playbackRate = rate;
    if (this.audioElement?.nativeElement) {
      this.audioElement.nativeElement.playbackRate = rate;
    }
  }

  formatTime(seconds: number): string {
    if (isNaN(seconds)) return '0:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Skip backward by specified seconds
   */
  rewind(seconds: number = this.skipBackwardSeconds) {
    if (!this.audioElement?.nativeElement) return;

    const audio = this.audioElement.nativeElement;
    const newTime = Math.max(0, audio.currentTime - seconds);
    audio.currentTime = newTime;

    // Visual feedback could be added here (e.g., toast notification)
    //console.log(`Skipped backward ${seconds}s to ${this.formatTime(newTime)}`);
  }

  /**
   * Skip forward by specified seconds
   */
  fastForward(seconds: number = this.skipForwardSeconds) {
    if (!this.audioElement?.nativeElement) return;

    const audio = this.audioElement.nativeElement;
    const newTime = Math.min(this.duration, audio.currentTime + seconds);
    audio.currentTime = newTime;

    // Visual feedback could be added here (e.g., toast notification)
    //console.log(`Skipped forward ${seconds}s to ${this.formatTime(newTime)}`);
  }

  /**
   * Restart audio from beginning
   */
  replay() {
    if (!this.audioElement?.nativeElement) return;

    const audio = this.audioElement.nativeElement;
    audio.currentTime = 0;

    // Auto-play after replay
    if (!this.isPlaying) {
      this.togglePlayPause();
    }
  }

  onClose() {
    this.cleanup();
    this.close.emit();
  }

  private cleanup() {
    if (this.audioElement?.nativeElement) {
      const audio = this.audioElement.nativeElement;
      audio.pause();

      // Clean up blob URL if it was created
      if (audio.src && audio.src.startsWith('blob:')) {
        URL.revokeObjectURL(audio.src);
      }

      // Remove event listeners
      audio.removeEventListener('loadstart', this.onLoadStart);
      audio.removeEventListener('loadedmetadata', this.onLoadedMetadata);
      audio.removeEventListener('canplay', this.onCanPlay);
      audio.removeEventListener('timeupdate', this.onTimeUpdate);
      audio.removeEventListener('ended', this.onEnded);
      audio.removeEventListener('error', this.onError);
      audio.removeEventListener('stalled', this.onStalled);
      audio.removeEventListener('waiting', this.onWaiting);

      audio.src = '';
      audio.load(); // Reset the audio element
    }

    this.isPlaying = false;
    this.isLoading = false;
    this.hasError = false;
    this.errorMessage = '';
    this.isAuthenticationError = false;
    this.currentTime = 0;
    this.duration = 0;
    this.progress = 0;
    this.originalRecordingUrl = '';
    this.showMoreMenu = false;
    this.showMetadataPanel = false;
    this.showKeyboardShortcuts = false;
  }

  /**
   * Download the recording with authentication
   */
  downloadRecording() {
    if (!this.originalRecordingUrl) {
      console.warn('No recording URL available for download');
      return;
    }

    const isLoading = this.isLoading;
    this.isLoading = true;

    this.exotelService.getRecordingBlob(this.originalRecordingUrl, true).subscribe({
      next: (blob: Blob) => {
        this.isLoading = isLoading;
        this.downloadBlob(blob, 'call-recording.mp3');
      },
      error: (error) => {
        console.error('Error downloading recording:', error);
        this.isLoading = isLoading;

        if (error.status === 403) {
          this.handleAuthenticationError();
        } else {
          alert('Failed to download recording. Please try again.');
        }
      }
    });
  }

  /**
   * Download blob as file
   */
  private downloadBlob(blob: Blob, filename: string) {
    try {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download recording.');
    }
  }
}
